// Roadmap 5.1 (A2-285) — a re-run of a period already filed is a correction
// of that return. Shows what changed against the return AS FILED, and how
// this country wants an error in a filed return corrected (route, what the
// filing shows, deadline, time limit), from the corrections grid.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { correctionOf } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";

const SHOWS = {
  FULL: ["ui.country_return.correction.shows_full", "File the whole corrected return for the original period."],
  DIFFERENCE: ["ui.country_return.correction.shows_difference", "File only the differences shown below."],
};
const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : v;
};

const Route = ({ r, testid }) => {
  const { t } = useTranslation();
  const shows = SHOWS[r.shows];
  return (
    <div data-testid={testid} className="text-sm text-slate-800 dark:text-slate-100 space-y-1">
      <p>{r.how}</p>
      {r.marker && <p className="text-xs"><span className="font-semibold">{t("ui.country_return.correction.on_the_form", { defaultValue: "On the form: " })}</span>{r.marker}</p>}
      <p className="text-xs">{shows && t(shows[0], { defaultValue: shows[1] })}</p>
      {r.note && <p className="text-xs text-slate-600 dark:text-slate-300">{r.note}</p>}
    </div>
  );
};

export const CorrectionPanel = ({ calcId, country }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [foundOn, setFoundOn] = useState("");
  const [box6, setBox6] = useState("");
  const [priorYear, setPriorYear] = useState(null);
  const [showAll, setShowAll] = useState(false);
  // Operator check 2026-10-16: a question stays on screen once asked. The
  // answer removes it from `needs`, which used to hide the field — so the
  // operator could not change the Box 6 they had typed.
  const [asked, setAsked] = useState([]);

  const load = useCallback(async () => {
    const params = {};
    if (foundOn) params.found_on = foundOn;
    if (box6.trim()) params.current_box6 = box6.trim();
    if (priorYear !== null) params.prior_year = priorYear;
    try {
      setErr(null);
      const d = await correctionOf(calcId, country, params);
      setData(d);
      const n = d?.recommendation?.needs || [];
      if (n.length) setAsked((prev) => [...new Set([...prev, ...n])]);
    }
    catch (e) { setErr(errorMessage(e)); }
  }, [calcId, country, foundOn, box6, priorYear]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div data-testid="correction-error" className="mb-4 text-xs text-rose-700 dark:text-rose-300">{err}</div>;
  if (!data || !data.is_correction) return null;
  const rec = data.recommendation || {};
  const rows = data.boxes.filter((b) => showAll || b.changed);
  const needs = rec.needs || [];
  const ask = (q) => needs.includes(q) || asked.includes(q);

  return (
    <section data-testid="correction-panel" className="mb-6 rounded-xl border border-amber-400 dark:border-amber-500/70 p-4 bg-amber-50 dark:bg-amber-950/30">
      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{t("ui.country_return.correction.title", { defaultValue: "Correcting a filed return" })}</h2>
      <p className="text-xs text-slate-700 dark:text-slate-300 mt-1">
        {data.filed.filing_reference
          ? t("ui.country_return.correction.redo_ref", { ref: data.filed.filing_reference, defaultValue: `This run re-does a period already filed (${data.filed.filing_reference}).` })
          : t("ui.country_return.correction.redo", { defaultValue: "This run re-does a period already filed." })}
        {" "}{t("ui.country_return.correction.vat_difference", { defaultValue: "VAT difference:" })} <span data-testid="correction-vat-difference" className="tabular-nums font-semibold">{money(data.vat_difference)}</span>
        {Number(data.vat_difference) > 0 ? t("ui.country_return.correction.more_due", { defaultValue: " more tax due." })
          : Number(data.vat_difference) < 0 ? t("ui.country_return.correction.less_due", { defaultValue: " less tax due." }) : "."}
        {!data.filed.stored && t("ui.country_return.correction.not_stored", { defaultValue: " The filed figures were not stored when it was filed, so they are re-calculated." })}
      </p>

      {data.changed_boxes === 0 && Number(data.vat_difference) === 0 && (
        <p data-testid="correction-nothing-changed" className="text-sm mt-2 font-medium">
          {t("ui.country_return.correction.nothing_changed", { defaultValue: "Nothing has changed against the filed return — there is nothing to correct." })}
        </p>
      )}
      <div className="mt-3 grid gap-3 sm:grid-cols-3 text-xs">
        <label className="flex flex-col gap-1">
          <span className="font-medium">{t("ui.country_return.correction.found_on", { defaultValue: "Date the error was found" })}</span>
          <input data-testid="correction-found-on" type="date" value={foundOn} onChange={(e) => setFoundOn(e.target.value)}
                 className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-base sm:text-xs" />
        </label>
        {ask("current_box6") && (
          <label className="flex flex-col gap-1">
            <span className="font-medium">{t("ui.country_return.correction.box6", { defaultValue: "Box 6 of the return you are doing now" })}</span>
            <input data-testid="correction-box6" inputMode="decimal" value={box6} onChange={(e) => setBox6(e.target.value)}
                   placeholder={t("ui.country_return.correction.box6_placeholder", { defaultValue: "e.g. 125000…" })} className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-base sm:text-xs tabular-nums" />
          </label>
        )}
        {ask("error_in_prior_year") && (
          <fieldset className="flex flex-col gap-1">
            <legend className="font-medium">{t("ui.country_return.correction.prior_year", { defaultValue: "Is the error in an earlier accounting year?" })}</legend>
            <div className="flex gap-3">
              <label><input type="radio" name="prior-year" checked={priorYear === true} onChange={() => setPriorYear(true)} /> {t("ui.country_return.correction.yes", { defaultValue: "Yes" })}</label>
              <label><input type="radio" name="prior-year" checked={priorYear === false} onChange={() => setPriorYear(false)} /> {t("ui.country_return.correction.no", { defaultValue: "No" })}</label>
            </div>
          </fieldset>
        )}
      </div>

      <div aria-live="polite" className="mt-3 space-y-2">
        {needs.length > 0 && rec.route ? (
          // Undecided until the question above is answered: show both
          // routes, never one of them as the answer (A2-395).
          <div data-testid="correction-undecided" className="space-y-2">
            <p className="text-sm font-medium">{t("ui.country_return.correction.undecided", { defaultValue: "Answer the question above to decide which applies. It is one of:" })}</p>
            <Route r={rec.route} testid="correction-route" />
            {rec.alternative && <Route r={rec.alternative} testid="correction-alternative" />}
          </div>
        ) : rec.route ? <Route r={rec.route} testid="correction-route" /> : rec.direction !== "none" && (
          <p data-testid="correction-no-route" className="text-sm">{rec.note || t("ui.country_return.correction.no_route", { defaultValue: "No correction route is recorded for this country." })}</p>
        )}
        {needs.length === 0 && rec.alternative && (
          <details className="text-xs"><summary className="cursor-pointer">{t("ui.country_return.correction.also_allowed", { defaultValue: "Also allowed" })}</summary><Route r={rec.alternative} testid="correction-alternative" /></details>
        )}
        {rec.deadline && rec.deadline.text && (
          <p data-testid="correction-deadline" className="text-xs font-semibold">
            {rec.deadline.text}{rec.deadline.date ? t("ui.country_return.correction.due_by", { date: rec.deadline.date, defaultValue: ` Due by ${rec.deadline.date}.` }) : ""}
          </p>
        )}
        {rec.limit && <p className="text-xs text-slate-600 dark:text-slate-300">{t("ui.country_return.correction.time_limit", { defaultValue: "Time limit:" })} {rec.limit}</p>}
        {rec.interest && <p className="text-xs text-slate-600 dark:text-slate-300">{t("ui.country_return.correction.interest", { defaultValue: "Interest and penalties:" })} {rec.interest}</p>}
        <p className="text-xs text-slate-600 dark:text-slate-300">
          {t("ui.country_return.correction.credit_note", { defaultValue: "A credit note issued later for a sale in that period is not a correction: it goes on the return for the period it is issued." })}
        </p>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table data-testid="correction-boxes" className="min-w-full text-xs tabular-nums">
          <thead><tr className="text-left text-slate-600 dark:text-slate-300">
            <th className="pr-3 py-1 font-medium">{t("ui.country_return.correction.col_box", { defaultValue: "Box" })}</th>
            <th className="pr-3 font-medium text-right">{t("ui.country_return.correction.col_filed", { defaultValue: "Filed" })}</th>
            <th className="pr-3 font-medium text-right">{t("ui.country_return.correction.col_corrected", { defaultValue: "Corrected" })}</th>
            <th className="font-medium text-right">{t("ui.country_return.authority_check.col_difference", { defaultValue: "Difference" })}</th>
          </tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.box_id} className="border-t border-amber-200/70 dark:border-amber-800/50">
                <td className="pr-3 py-1">
                  {b.display_ref ? `Box ${b.display_ref}` : b.label_english}{/* i18n-ignore */}
                  {b.form_line ? <span className="ml-1 text-slate-500">line {b.form_line}{/* i18n-ignore */}</span> : null}
                </td>
                <td className="pr-3 text-right">{money(b.filed)}</td>
                <td className="pr-3 text-right">{money(b.corrected)}</td>
                <td className={`text-right ${b.changed ? "font-semibold" : ""}`}>{money(b.difference)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={() => setShowAll(!showAll)} className="mt-2 text-xs underline">
          {showAll
            ? t("ui.country_return.correction.show_changed", { defaultValue: "Show only the boxes that changed" })
            : t("ui.country_return.correction.show_all", { count: data.boxes.length, defaultValue: `Show all ${data.boxes.length} boxes` })}
        </button>
      </div>
    </section>
  );
};
